/**
 * Banner Service
 *
 * Fetches banner data from the Strapi API using the central UniversalApiClient.
 */
import UniversalApiClient from './UniversalApiClient';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || process.env.REACT_APP_BACKEND_URL;

class BannerService {
  /**
   * Get all active banners from Strapi
   */
  static async getBanners() {
    try {
      console.log('[BannerService.getBanners] Fetching banners from Strapi...');
      console.log('[BannerService.getBanners] STRAPI_URL:', STRAPI_URL);
      
      // Strapi v4 format for populate
      const response = await UniversalApiClient.strapiGet('/banners', {
        'populate': 'image'
      });

      console.log('[BannerService.getBanners] Full Response:', JSON.stringify(response, null, 2));

      if (!response.success) {
        const errorMsg = response.error?.message || 'Failed to fetch banners';
        console.error('[BannerService.getBanners] Request failed:', errorMsg);
        console.error('[BannerService.getBanners] Error details:', response.error);
        return [];
      }

      // Handle different response structures
      let bannerData = [];
      
      // Check if data is in response.data.data (Strapi v4 format)
      if (response.data && response.data.data) {
        bannerData = response.data.data;
        console.log('[BannerService.getBanners] Found data in response.data.data:', bannerData.length);
      } 
      // Check if data is directly in response.data (already unwrapped)
      else if (response.data && Array.isArray(response.data)) {
        bannerData = response.data;
        console.log('[BannerService.getBanners] Found data directly in response.data:', bannerData.length);
      }
      // Check if data is at root level
      else if (Array.isArray(response)) {
        bannerData = response;
        console.log('[BannerService.getBanners] Found data at root level:', bannerData.length);
      }
      else {
        console.warn('[BannerService.getBanners] Unexpected response structure:', response);
        return [];
      }

      if (!bannerData || bannerData.length === 0) {
        console.warn('[BannerService.getBanners] No banners found in database');
        return [];
      }

      console.log('[BannerService.getBanners] Raw banner data:', JSON.stringify(bannerData, null, 2));

      // Transform the Strapi response data
      const banners = bannerData.map((item, index) => {
        console.log(`[BannerService.getBanners] Processing banner ${index}:`, item);
        
        // Handle both Strapi v4 format (with attributes) and direct format
        const attributes = item.attributes || item;
        const imageData = attributes.image?.data || attributes.image;
        const imageAttributes = imageData?.attributes || imageData;
        const imageUrl = imageAttributes?.url;

        // Handle both relative and absolute URLs
        let fullImageUrl = null;
        if (imageUrl) {
          // If URL already starts with http:// or https://, use it as-is
          if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            fullImageUrl = imageUrl;
          } else {
            // Otherwise, prepend the STRAPI_URL
            fullImageUrl = `${STRAPI_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
          }
        }

        const banner = {
          id: item.id || index,
          title: attributes.title || 'Untitled Banner',
          description: attributes.description || '',
          imageUrl: fullImageUrl,
          link: attributes.link || null,
          order: attributes.order || 0,
        };

        console.log(`[BannerService.getBanners] Transformed banner ${index}:`, banner);
        return banner;
      });

      // Sort by order
      banners.sort((a, b) => a.order - b.order);

      console.log('[BannerService.getBanners] Final banners array:', banners);
      return banners;
    } catch (error) {
      console.error('[BannerService.getBanners] Exception caught:', error);
      console.error('[BannerService.getBanners] Error stack:', error.stack);
      // Return empty array instead of throwing to prevent app crash
      return [];
    }
  }
}

export default BannerService;
